

















































import { Vue, Component, Prop } from 'vue-property-decorator';

import { getLabelInfo } from '@/utils/getLabelInfo';
import { StartGuideTask } from '@/models/start-guide';
import { RewardsProgramFormState } from '@/models/rewards-program';
import { IBusinessType } from '@/models/business';

import CloseModalIcon from '@/components/icons/CloseModal.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import NavButtonItem from '@/componentsV2/molecules/NavButtonItem/NavButtonItem.vue';

// Step components
import Introduction from './Steps/Introduction.vue';
import Ilustra from './Steps/Ilustra.vue';
import IntroductionPOSSystem from './Steps/IntroductionPOSSystem.vue';
import FeedbackIlustra from './Steps/FeedbackIlustra.vue';
import Operation, { OPERATION_TYPE_STORAGE_KEY } from './Steps/Operation.vue';
import RewardRatio from './Steps/RewardRatio.vue';
import RefundPolicy from './Steps/RefundPolicy.vue';
import FeedbackStatus from './Steps/FeedbackStatus.vue';
import { impliedLoyaltyOperation } from '@/utils/loyaltyOperation';
import segmentIo from '@/utils/segment.io';
import BillingModel from '@/views/Models/BillingModel.vue';
import eventBus from '@/utils/eventBus';

type OperationTypeWithoutBoth = Exclude<IBusinessType, IBusinessType.BOTH>;

export type RewardsProgramStep = {
  slug: string;
  label: string;
  activePercentage?: number;
  routes: Array<string>;
};

export type RewardsProgramNextParams = {
  redirectToSlug: string;
};

export enum ERewardsProgramStepSlug {
  INTRODUCTION = 'introduction',
  OPERATION_POS_SYSTEM = 'operation:pos-system',
  OPERATION = 'operation',
  REWARD_RATIO = 'reward-ratio',
  REFUND_POLICY = 'refund-policy',
  FEEDBACK_STATUS = 'feedback-status'
}

export const FEATURE_REWARD_STORAGE_KEY = '@twism:feature-reward';

@Component({
  components: {
    Ilustra,
    FeedbackIlustra,
    CloseModalIcon,
    FilledButton,
    NavButtonItem,
    Introduction,
    IntroductionPOSSystem,
    RewardRatio,
    RefundPolicy,
    FeedbackStatus,
    Operation
  }
})
export default class RewardsProgramModal extends BillingModel {
  @Prop() task!: StartGuideTask;
  @Prop() operationType!: OperationTypeWithoutBoth;
  @Prop() feature!: string;
  @Prop() onConfirm!: () => void;
  @Prop({ default: () => ERewardsProgramStepSlug.INTRODUCTION })
  initialStep!: ERewardsProgramStepSlug;

  ERewardsProgramStepSlug = ERewardsProgramStepSlug;

  flowType = this.initialFlowType;

  activeStepSlug: string = this.initialStep;
  loading = false;

  get steps(): RewardsProgramStep[] {
    return [
      {
        slug: ERewardsProgramStepSlug.INTRODUCTION,
        label: 'Introduction',
        routes: ['introduction']
      },
      {
        slug: ERewardsProgramStepSlug.OPERATION,
        label: `Operation`,
        routes: [
          this.showChooseOperation ? 'operation' : '',
          !this.currentProvider ? 'operation:pos-system' : ''
        ].filter(Boolean),
        inactive:
          this.currentProvider &&
          this.currentProvider.connected &&
          this.initialStep !== ERewardsProgramStepSlug.REWARD_RATIO &&
          this.businessInfo.coinRewardPercentage !== '0.00'
      },
      {
        slug: ERewardsProgramStepSlug.REWARD_RATIO,
        label: `Reward rate`,
        routes: ['reward-ratio']
      },
      {
        slug: ERewardsProgramStepSlug.REFUND_POLICY,
        label: 'Refund policy',
        routes: ['refund-policy']
      }
    ].filter(item => item.routes.filter(item => !!item)[0] && !item.inactive);
  }

  get needsOperation() {
    const hasRewardConfig =
      this.businessInfo.coinRewardPercentage &&
      this.businessInfo.coinRewardPercentage !== '0.00';

    return (
      this.businessInfo.type === IBusinessType.BOTH &&
      !hasRewardConfig &&
      this.businessInfo.operationType === this.businessInfo.type
    );
  }

  get impliedOperation() {
    return this.needsOperation
      ? impliedLoyaltyOperation(this.businessInfo.type)
      : undefined;
  }

  get showChooseOperation() {
    return this.needsOperation && !this.impliedOperation;
  }

  get currentProvider() {
    return this.$store.getters[
      'merchant/getConnectedProviderWithWidgetSettings'
    ];
  }

  get componentByStepSlug(): {
    [k: string]: {
      showNav: boolean;
      ilustra: string;
      ilustraComponent?: string;
      component: string;
    };
  } {
    return {
      [ERewardsProgramStepSlug.INTRODUCTION]: {
        component: 'Introduction',
        ilustra: 'introduction',
        ilustraComponent: '',
        showNav: true
      },
      [ERewardsProgramStepSlug.OPERATION_POS_SYSTEM]: {
        component: 'IntroductionPOSSystem',
        ilustra:
          this.flowType === IBusinessType.INSTORE
            ? 'introduction-pos-system'
            : 'introduction-platform',
        ilustraComponent: '',
        showNav: true
      },
      [ERewardsProgramStepSlug.OPERATION]: {
        component: 'Operation',
        ilustra: 'introduction',
        ilustraComponent: '',
        showNav: true
      },
      [ERewardsProgramStepSlug.REWARD_RATIO]: {
        component: 'RewardRatio',
        ilustra: 'reward-ratio',
        ilustraComponent: '',
        showNav: true
      },
      [ERewardsProgramStepSlug.REFUND_POLICY]: {
        component: 'RefundPolicy',
        ilustra: 'refund-policy',
        ilustraComponent: '',
        showNav: true
      },
      [ERewardsProgramStepSlug.FEEDBACK_STATUS]: {
        component: 'FeedbackStatus',
        ilustra: '',
        ilustraComponent: 'FeedbackIlustra',
        showNav: false
      }
    };
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'] || {};
  }

  get initialFlowType() {
    if (this.impliedOperation) return this.impliedOperation;

    if (!this.operationType) {
      const { operationType } = this.rewardsProgramFormData;
      if (operationType) return operationType;

      return this.businessInfo.type;
    }

    return this.operationType;
  }

  get rewardsProgramFormData() {
    return this.$store.getters[
      'rewardsProgram/getFormData'
    ] as RewardsProgramFormState;
  }

  created() {
    if (this.inactiveBilling) {
      this.close();
      return;
    }

    this.resizeEventHandler();
    // The Refund Policy step sends this to the back end, so it must survive
    // the provider OAuth reload, as the operation step's own choice does.
    if (this.impliedOperation) {
      this.$store.dispatch('rewardsProgram/SET_FORM_DATA', {
        operationType: this.impliedOperation
      });
      localStorage.setItem(OPERATION_TYPE_STORAGE_KEY, this.impliedOperation);
    }
    if (this.feature)
      sessionStorage.setItem(FEATURE_REWARD_STORAGE_KEY, this.feature);

    segmentIo.modal('Rewards Program Set Up');
  }

  mounted() {
    if (this.inactiveBilling) {
      return;
    }
    document.onkeydown = this.handleOnkeydown;

    window.addEventListener('popstate', () => {
      this.handleClose();
    });

    if (this.rewardsProgramFormData.address) return;

    this.loadBusinessAddress();
  }

  close() {
    eventBus.$emit('closeModal');
  }

  beforeDestroy() {
    if (this.inactiveBilling) {
      return;
    }
    window.removeEventListener('resize', this.resizeEventHandler);
    // Left behind, it would be sent for the next business set up on this
    // browser. An OAuth redirect unloads the page without destroying the modal.
    if (this.impliedOperation)
      localStorage.removeItem(OPERATION_TYPE_STORAGE_KEY);
    this.$store.dispatch('rewardsProgram/SET_FORM_DATA', {
      operationType: undefined
    });
  }

  handleChangeFlowType(flowType: IBusinessType) {
    this.flowType = flowType;
  }

  percentageConcluded(step: RewardsProgramStep) {
    const indexOfStep = this.steps.findIndex(
      refStep => refStep.slug === step.slug
    );

    const currentIndex = this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug.split(':')[0]
    );

    const routesAmount = step.routes.length;
    const internalActiveIndex = step.routes.findIndex(
      rouName => rouName === this.activeStepSlug
    );
    const fraction = 100 / routesAmount;

    const isPrevLink = indexOfStep < currentIndex;

    return isPrevLink
      ? 100
      : (internalActiveIndex * 100) / routesAmount + fraction;
  }

  isActiveStep(stepSlug: string) {
    const [mainStep] = this.activeStepSlug.split(':');

    return stepSlug.includes(mainStep);
  }

  handleOnkeydown({ key }: KeyboardEvent) {
    if (key === 'Escape') {
      this.handleClose();
    }
  }

  resizeEventHandler() {
    const vh = window.innerHeight * 0.01;

    document.documentElement.style.setProperty('--rewards-modal-vh', `${vh}px`);
  }

  loadBusinessAddress() {
    const businessState = this.$store.getters['business/getBusinessState'];

    const location = businessState.businessLocations[0];

    if (!location) return;

    const fullAddress = getLabelInfo(
      ['address', 'city', 'state', 'zip', 'country'],
      location
    );

    const address = {
      address: location.address,
      cityName: location.city,
      countryCode: location.countryIso2,
      countryName: location.country,
      fullAddress: fullAddress,
      postCode: location.zip,
      stateCode: location.state,
      stateName: location.stateName
    };

    this.$store.dispatch('rewardsProgram/SET_FORM_DATA', {
      address,
      aditionalAddress: location.addressAdditional
    });
  }

  clearRoute() {
    this.$router.replace({
      query: {
        ...this.$route.query,
        modal: undefined
      }
    });
  }

  handleClose() {
    this.onConfirm();
    this.clearRoute();
  }

  handleConfirm() {
    this.onConfirm();
    this.clearRoute();
  }

  handleGoToStep({ redirectToSlug }: RewardsProgramNextParams) {
    segmentIo.track('Changed Step Rewards Program Set Up', {
      'Actual Step': this.activeStepSlug,
      'Go To Step': redirectToSlug,
      'Business Type': this.businessInfo.type
    });
    this.activeStepSlug = redirectToSlug;
  }

  onClickOutside(event: any) {
    if (event.target.className.includes('modal')) {
      this.handleClose();
    }

    return;
  }
}
